import { useCallback, useEffect, useState } from 'react'
import { handleError } from '../../../../lib/utils'
import {
  createClinicalNote,
  groupData,
  readClinicalEntries,
  readClinicalNotes,
  updateClinicalNote
} from '../../../examination-utils/examination-lib'
import { useSelector } from 'react-redux'
import { message } from 'antd'
import { generateAIResponse } from '../../../../AI Tools/assistants-lib'
import { assistantInitiate } from '../../../../AI Tools/utils'
import { AssistantEnums } from '../../../../AI Tools/assitants-enums'
import { readMedicalHistory } from '../../../../Admin/patients/utils/utils'
import { debounce } from 'lodash'
import { DEBOUNCE_DELAY } from '../utils/constants'

export const useNoteState = ({ bookingData, userRef }) => {
  const [transcriptionState, setTranscriptionState] = useState({
    isListening: false,
    isRecording: false,
    tmpTranscript: ''
  })
  const [noteState, setNoteState] = useState({
    currentNote: '',
    noteId: null,
    isAutoGenerating: false,
    isSubmitting: true, // Start as true to prevent race condition
    isUpdating: false,
    isCreating: false,
    fetchingError: '',
    updatingError: ''
  })

  const authState = useSelector((state) => state.auth)
  const [showClinicalNotesTemplates, setShowClinicalNotesTemplates] = useState(false)
  const [showHistorical, setShowHistorical] = useState(false)
  const [showAiTranscription, setShowAiTranscription] = useState(false)

  const checkForExistingNote = useCallback(async () => {
    // Need either bookingData._id or userRef to fetch notes
    if ((!bookingData?._id && !userRef) || !authState?.agiliteUser?._id) {
      console.log('🔍 checkForExistingNote: Missing required data', {
        bookingId: bookingData?._id,
        userRef: userRef,
        userId: authState?.agiliteUser?._id
      })
      // Set isSubmitting to false when missing required data to allow note creation
      setNoteState((prev) => ({ ...prev, isSubmitting: false }))
      return
    }

    setNoteState((prev) => ({ ...prev, isSubmitting: true }))
    try {
      // Create query based on available data
      const query = {
        createdBy: authState.agiliteUser._id
      }

      // If we have bookingData, search by booking reference
      if (bookingData?._id) {
        query.bookingRef = bookingData._id
      } else if (userRef) {
        // If no booking but we have userRef, search by userRef
        query.userRef = userRef
      }

      const existingNotes = await readClinicalNotes(query)

      // Filter out empty notes and find the latest note with actual content
      const notesWithContent =
        existingNotes?.filter((note) => note.content && note.content.trim() !== '' && note.content !== '<p><br></p>') ||
        []
      const latestNoteWithContent = notesWithContent[notesWithContent.length - 1]

      if (latestNoteWithContent) {
        console.log('✅ checkForExistingNote: Loading latest note with content', {
          noteId: latestNoteWithContent._id,
          contentLength: latestNoteWithContent.content?.length,
          content: latestNoteWithContent.content?.substring(0, 50) + '...'
        })
        setNoteState((prev) => ({
          ...prev,
          currentNote: latestNoteWithContent.content || '',
          noteId: latestNoteWithContent._id,
          fetchingError: ''
        }))
      } else {
      }
    } catch (error) {
      console.error('❌ checkForExistingNote: Error fetching notes', error)
      setNoteState((prev) => ({
        ...prev,
        fetchingError: handleError(error, true)
      }))
    } finally {
      setNoteState((prev) => ({ ...prev, isSubmitting: false }))
    }
  }, [bookingData?._id, userRef, authState?.agiliteUser?._id])

  useEffect(() => {
    checkForExistingNote()
  }, [checkForExistingNote]) // Now properly depends on the memoized function

  const handleCreateNote = useCallback(async () => {
    if (!bookingData && !userRef) {
      message.error('Missing booking or patient information')
      return
    }

    setNoteState((prev) => ({ ...prev, isCreating: true }))
    try {
      const newNote = await createClinicalNote('', bookingData, userRef)
      if (!newNote?._id) {
        throw new Error('Failed to create new note')
      }

      setNoteState((prev) => ({
        ...prev,
        noteId: newNote._id,
        currentNote: '',
        isCreating: false
      }))
    } catch (error) {
      console.error('Failed to create clinical note:', error)
      message.error(handleError(error))
      setNoteState((prev) => ({ ...prev, isCreating: false }))
    }
  }, [bookingData, userRef])

  const handleUpdateNote = useCallback(
    async (noteId, newNote) => {
      if (!noteId || typeof newNote !== 'string') return

      try {
        await updateClinicalNote(noteId, newNote, bookingData?._id, bookingData?.userRef)
        setNoteState((prev) => ({ ...prev, updatingError: '' }))
      } catch (error) {
        console.error('Failed to update clinical note:', error)
        setNoteState((prev) => ({ ...prev, updatingError: handleError(error) }))
      } finally {
        setNoteState((prev) => ({ ...prev, isUpdating: false }))
      }
    },
    [bookingData?._id, bookingData?.userRef]
  )

  useEffect(() => {
    setTranscriptionState((prev) => ({ ...prev, tmpTranscript: '' }))
  }, [noteState.currentNote])

  // Create debounced function with proper dependencies to prevent stale closures
  const debouncedNoteCapture = useCallback(
    debounce(async (noteId, newNote) => {
      await handleUpdateNote(noteId, newNote)
    }, DEBOUNCE_DELAY),
    [handleUpdateNote]
  )

  const handleAutoGenerateNotes = async ({ language = 'English', additionalPrompt = '' } = {}) => {
    if (!bookingData?._id || !bookingData?.userRef) {
      message.error('Missing required booking information')
      return
    }

    try {
      setNoteState((prev) => ({ ...prev, isAutoGenerating: true }))

      const [tmpClinicalData, medicalHistory] = await Promise.all([
        readClinicalEntries({ userRef: bookingData?.userRef || userRef }),
        readMedicalHistory({ userRef: bookingData?.userRef || userRef })
      ])

      const payload = {
        patientDetails: bookingData?.patientRecord,
        clinicalData: groupData(tmpClinicalData),
        medicalHistory,
        existingContent: noteState.currentNote,
        language,
        additionalPrompt
      }

      const languageInstruction = `Please generate a clinical note in ${language} based on the following patient data. ${
        additionalPrompt ? `Additional instructions: ${additionalPrompt}` : ''
      }

Patient Data: ${JSON.stringify(payload)}`

      const tmpData = await assistantInitiate(languageInstruction, AssistantEnums.assistant_ids.CLINICAL_NOTES)
      const aiResponse = await generateAIResponse(tmpData)
      const tmpContent = aiResponse?.data?.data[0]?.content[0]?.text?.value

      if (!tmpContent) {
        throw new Error('Failed to generate AI content')
      }

      setNoteState((prev) => ({
        ...prev,
        isAutoGenerating: false,
        isUpdating: true,
        currentNote: tmpContent
      }))

      await debouncedNoteCapture(noteState.noteId, tmpContent)
    } catch (error) {
      message.error(handleError(error))
      setNoteState((prev) => ({ ...prev, isAutoGenerating: false }))
    }
  }

  return {
    noteState,
    setNoteState,
    showClinicalNotesTemplates,
    setShowClinicalNotesTemplates,
    showHistorical,
    setShowHistorical,
    checkForExistingNote,
    handleCreateNote,
    handleUpdateNote,
    showAiTranscription,
    setShowAiTranscription,
    handleAutoGenerateNotes,
    transcriptionState,
    debouncedNoteCapture
  }
}
